import React, { useEffect, useState, ReactNode } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'

interface TextRevealProps {
  children: ReactNode
  className?: string
  delay?: number
  duration?: number
  stagger?: number
}

export const TextReveal: React.FC<TextRevealProps> = ({
  children,
  className = '',
  delay = 0,
  duration = 0.6,
  stagger = 0.03
}) => {
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true
  })

  // If children is a string, split into words for animation
  if (typeof children === 'string') {
    const words = children.split(' ')

    return (
      <div ref={ref} className={className}>
        {words.map((word, i) => (
          <motion.span
            key={i}
            className="inline-block mr-2"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{
              duration,
              delay: delay + i * stagger,
              ease: [0.6, -0.05, 0.01, 0.99]
            }}
          >
            {word}
          </motion.span>
        ))}
      </div>
    )
  }

  // For non-string children (JSX), animate the whole block
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{
        duration,
        delay,
        ease: [0.6, -0.05, 0.01, 0.99]
      }}
    >
      {children}
    </motion.div>
  )
}

interface TypewriterProps {
  texts: string[]
  className?: string
  speed?: number
  deleteSpeed?: number
  delayBetween?: number
}

export const Typewriter: React.FC<TypewriterProps> = ({
  texts,
  className = '',
  speed = 100,
  deleteSpeed = 50,
  delayBetween = 2000
}) => {
  const [displayText, setDisplayText] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const currentText = texts[currentIndex]
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentText.length) {
          setDisplayText(currentText.slice(0, displayText.length + 1))
        } else {
          // Start deleting after delay
          setTimeout(() => setIsDeleting(true), delayBetween)
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1))
        } else {
          setIsDeleting(false)
          setCurrentIndex((prev) => (prev + 1) % texts.length)
        }
      }
    }, isDeleting ? deleteSpeed : speed)

    return () => clearTimeout(timeout)
  }, [displayText, currentIndex, isDeleting, texts, speed, deleteSpeed, delayBetween])

  return (
    <span className={className}>
      {displayText}
      <motion.span
        animate={{ opacity: [0, 1] }}
        transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
        className="inline-block w-0.5 h-[1em] bg-current ml-1"
      />
    </span>
  )
}

interface CountUpProps {
  end: number
  start?: number
  duration?: number
  suffix?: string
  prefix?: string
  className?: string
}

export const CountUp: React.FC<CountUpProps> = ({
  end,
  start = 0,
  duration = 2,
  suffix = '',
  prefix = '',
  className = ''
}) => {
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true
  })

  const [count, setCount] = useState(start)

  useEffect(() => {
    if (!inView) return

    let startTime: number
    let animationFrame: number

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp
      
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1)
      const easeOutCubic = 1 - Math.pow(1 - progress, 3)
      
      setCount(Math.floor(start + (end - start) * easeOutCubic))
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate)
      }
    }

    animationFrame = requestAnimationFrame(animate)

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [inView, start, end, duration])

  return (
    <span ref={ref} className={className}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  )
}

interface GlitchTextProps {
  children: string
  className?: string
}

export const GlitchText: React.FC<GlitchTextProps> = ({
  children,
  className = ''
}) => {
  return (
    <div className={`relative ${className}`}>
      <span className="relative z-10">{children}</span>
      <span 
        className="absolute top-0 left-0 w-full h-full text-red-500 animate-pulse opacity-0 hover:opacity-100 transition-opacity"
        style={{ 
          clipPath: 'inset(40% 0 60% 0)',
          transform: 'translate(-2px, -2px)',
        }}
      >
        {children}
      </span>
      <span 
        className="absolute top-0 left-0 w-full h-full text-cyan-500 animate-pulse opacity-0 hover:opacity-100 transition-opacity"
        style={{ 
          clipPath: 'inset(60% 0 40% 0)',
          transform: 'translate(2px, 2px)',
        }}
      >
        {children}
      </span>
    </div>
  )
}
